import { showErrorToast, showSuccessToast } from "@utils/toast"

/**
 * Production has no direct DB access from a developer machine, so the way a prod incident
 * reaches a local investigation is this envelope: the admin panel already holds the whole
 * audit document, so it exports it verbatim and the `call-run-debug` / `ai-run-debug`
 * skills read it back as the source of truth instead of querying Mongo.
 *
 * The envelope is deliberately self-describing (kind + id + which environment it came
 * from): a bare `logs[]` array pasted into a file loses the one thing an investigation
 * cannot re-derive later, which is where and when it was captured.
 */
export const DEBUG_BUNDLE_VERSION = 1

export type DebugBundleKind = "call" | "ai-run" | "operator-run"

export type DebugBundle<TData> = {
  bundleVersion: number
  kind: DebugBundleKind
  id: string
  capturedAt: string
  source: {
    app: "admin-panel-v2"
    /** Best-effort environment label — the api url is the only thing the panel knows. */
    env: string
    apiUrl?: string
  }
  data: TData
}

/**
 * `ENV` is set per `.env.<stage>` file, `NODE_ENV` only ever says development/production,
 * so the api url is the tie-breaker that tells prod from beta/gamma.
 */
function resolveEnvLabel(): string {
  const explicit = process.env.ENV
  if (explicit) return explicit

  const apiUrl = process.env.REACT_APP_API_URL || ""
  const match = /https?:\/\/([^./]+)/.exec(apiUrl)
  return match?.[1] || process.env.NODE_ENV || "unknown"
}

export function buildDebugBundle<TData>(params: {
  kind: DebugBundleKind
  id: string
  data: TData
  capturedAt?: string
}): DebugBundle<TData> {
  return {
    bundleVersion: DEBUG_BUNDLE_VERSION,
    kind: params.kind,
    id: params.id,
    capturedAt: params.capturedAt || new Date().toISOString(),
    source: {
      app: "admin-panel-v2",
      env: resolveEnvLabel(),
      apiUrl: process.env.REACT_APP_API_URL
    },
    data: params.data
  }
}

function slugify(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64) || "unknown"
}

/** `call-CAxxxx-20260819-141005.json` — sorts chronologically inside the drop folder. */
export function debugBundleFileName(bundle: DebugBundle<unknown>): string {
  const stamp = bundle.capturedAt.replace(/[-:]/g, "").replace(/\..*$/, "")
  const [date, time] = stamp.split("T")
  return `${bundle.kind}-${slugify(bundle.id)}-${date}-${time}.json`
}

export function serializeDebugBundle(bundle: DebugBundle<unknown>): string {
  return JSON.stringify(bundle, null, 2)
}

export async function copyDebugBundle(
  bundle: DebugBundle<unknown>
): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(serializeDebugBundle(bundle))
    showSuccessToast(
      `Bundle kopyalandı — ${debugBundleFileName(bundle)} olarak kaydedebilirsin`
    )
    return true
  } catch {
    showErrorToast("Bundle kopyalanamadı")
    return false
  }
}

export function downloadDebugBundle(bundle: DebugBundle<unknown>): boolean {
  try {
    const blob = new Blob([serializeDebugBundle(bundle)], {
      type: "application/json"
    })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = debugBundleFileName(bundle)
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
    showSuccessToast(`Bundle indirildi: ${debugBundleFileName(bundle)}`)
    return true
  } catch {
    showErrorToast("Bundle indirilemedi")
    return false
  }
}
